import { useCallback, useEffect, useRef } from 'react';
import { useAnalytics } from './useAnalytics';
import { AnalyticsEvent } from '../constants/analyticsEvents';
import { useShallow } from 'zustand/react/shallow';
import { useStoreInfoStore } from '../../stores/useStoreInfoStore';
import { sendConversionEvent } from '../services/chat-api.service';
import { purchaseDetector } from '../services/purchase-detector';
import type { PurchaseEvent } from '../services/purchase-detector';
import type { StoreConfig } from '../services/types';

interface UsePurchaseTrackingOptions {
  enabled: boolean;
  platformType?: StoreConfig['platformType'];
  purchaseAutoDetectEnabled?: boolean;
  superbarVisible?: boolean;
  isLoadingFlags?: boolean;
}

export const usePurchaseTracking = ({
  enabled,
  platformType,
  purchaseAutoDetectEnabled,
  superbarVisible,
  isLoadingFlags,
}: UsePurchaseTrackingOptions): void => {
  const { trackEvent } = useAnalytics();
  const { store, currentSession } = useStoreInfoStore(
    useShallow((s) => ({ store: s.store, currentSession: s.currentSession }))
  );
  const hasFired = useRef(new Set<string>());

  const sendPurchaseEvent = useCallback(
    async (purchase: PurchaseEvent) => {
      if (!store || !currentSession) return;
      if (purchase.orderId && hasFired.current.has(purchase.orderId)) return;
      if (purchase.orderId) hasFired.current.add(purchase.orderId);

      trackEvent(AnalyticsEvent.PURCHASE_DETECTED, {
        source: purchase.source,
        hasValue: purchase.value !== undefined,
        superbarVisible,
      });

      try {
        const eventData: Record<string, unknown> = {
          orderValue: purchase.value,
          currency: purchase.currency,
          source: purchase.source,
        };
        if (purchase.orderId) {
          eventData.orderId = purchase.orderId;
        }

        await sendConversionEvent({
          storeId: store.id,
          session: currentSession,
          data: { ...eventData, purchaseAutoDetected: true, superbarVisible },
        });
      } catch (error) {
        console.error('Purchase tracking failed:', error);
      }
    },
    [store, currentSession, trackEvent, superbarVisible]
  );

  useEffect(() => {
    if (
      !enabled ||
      !purchaseAutoDetectEnabled ||
      !store ||
      !currentSession ||
      isLoadingFlags
    ) {
      return;
    }

    purchaseDetector.start(platformType);

    // Drain pre-existing events (Shopify checkout + dataLayer entries before mount)
    for (const purchase of purchaseDetector.drainQueue()) {
      sendPurchaseEvent(purchase);
    }

    // Listen for late-arriving events (GA4 push after mount)
    return purchaseDetector.onPurchase(sendPurchaseEvent);
  }, [
    enabled,
    purchaseAutoDetectEnabled,
    platformType,
    sendPurchaseEvent,
    store,
    currentSession,
    isLoadingFlags,
  ]);
};
